import React, { useState } from 'react';
import type { GroupedRowData, DataGridColumn } from '../DataGrid.types';
import type { AggregationConfig } from '../../utils/aggregations';
import { formatAggregation } from '../../utils/aggregations';

import type { DensityType } from './TableHeader';

interface GroupedRowProps<T extends Record<string, unknown>> {
  group: GroupedRowData<T>;
  columns: DataGridColumn<T>[];
  isExpanded: boolean;
  onToggle: () => void;
  aggregations: AggregationConfig;
  // Column reorder
  draggedColumnId?: string | null;
  getColumnStyle?: (columnId: string) => React.CSSProperties;
  // Enterprise Finance & B2B
  density?: DensityType;
  // Grouping display options
  showRowCount?: boolean;
}

// Chevron Icon
const ChevronIcon = ({ isExpanded }: { isExpanded: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
      transition: 'transform var(--transition-fast)'
    }}
  >
    <path d="M6 4l4 4-4 4" />
  </svg>
);

export function GroupedRow<T extends Record<string, unknown>>({
  group,
  columns,
  isExpanded,
  onToggle,
  aggregations,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  draggedColumnId: _draggedColumnId,
  getColumnStyle,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  density: _density = 'normal',
  showRowCount = true
}: GroupedRowProps<T>): React.ReactElement {
  const [isHovered, setIsHovered] = useState(false);
  const indent = group.level * 24;

  return (
    <tr
      onClick={onToggle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: isHovered
          ? 'var(--color-neutral-200)'
          : 'var(--color-neutral-100)',
        cursor: 'pointer',
        fontWeight: 500,
        transition: 'background var(--transition-fast)',
        borderLeft: isExpanded ? '3px solid var(--color-primary-500)' : '3px solid transparent'
      }}
    >
      {columns.map((column, index) => {
        const columnDragStyle = getColumnStyle?.(column.id) || {};

        if (index === 0) {
          return (
            <td
              key={column.id}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                paddingLeft: `${indent + 16}px`,
                fontSize: 'var(--text-sm)',
                borderBottom: '1px solid var(--color-border-default)',
                borderRight: '1px solid var(--color-border-subtle)',
                position: 'relative',
                background: isHovered
                  ? 'var(--color-neutral-200)'
                  : 'var(--color-neutral-100)',
                ...columnDragStyle
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)'
                }}
              >
                {/* Indentation Guide Lines */}
                {group.level > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      left: `${(group.level - 1) * 24 + 20}px`,
                      top: 0,
                      bottom: 0,
                      width: '1px',
                      background: 'var(--color-border-default)'
                    }}
                  />
                )}

                {/* Chevron */}
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '20px',
                    height: '20px',
                    borderRadius: 'var(--radius-sm)',
                    background: isExpanded ? 'var(--color-primary-100)' : 'transparent',
                    color: isExpanded ? 'var(--color-primary-600)' : 'var(--color-text-secondary)',
                    transition: 'all var(--transition-fast)'
                  }}
                >
                  <ChevronIcon isExpanded={isExpanded} />
                </span>

                {/* Group Value */}
                <span style={{ color: 'var(--color-text-primary)' }}>
                  {String(group.groupValue)}
                </span>

                {/* Row Count Badge */}
                {showRowCount && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      padding: 'var(--space-1) var(--space-2)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 500,
                      borderRadius: 'var(--radius-full)',
                      background: 'var(--color-primary-100)',
                      color: 'var(--color-primary-700)'
                    }}
                  >
                    {group.rowCount}
                  </span>
                )}
              </div>
            </td>
          );
        }

        const accessorKey = column.accessorKey || column.id;
        const aggValue = group.aggregatedValues[accessorKey];
        const aggType = aggregations[accessorKey];

        return (
          <td
            key={column.id}
            style={{
              padding: 'var(--space-3) var(--space-4)',
              fontSize: 'var(--text-sm)',
              color: aggValue !== undefined ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)',
              borderBottom: '1px solid var(--color-border-default)',
              borderRight: '1px solid var(--color-border-subtle)',
              fontVariantNumeric: 'tabular-nums',
              position: 'relative',
              background: isHovered
                ? 'var(--color-neutral-200)'
                : 'var(--color-neutral-100)',
              ...columnDragStyle
            }}
          >
            {aggValue !== undefined && aggType
              ? formatAggregation(aggValue, aggType)
              : ''}
          </td>
        );
      })}
    </tr>
  );
}
